/**
 * API Client Interface
 *
 * Platform-agnostic API client definition.
 * Actual implementation provided by platform-specific packages (@web, @rn).
 */

import type { z } from 'zod';

import type { ApiResponse } from './types';

// Re-export for convenience
export type { ApiResponse };

/**
 * API Error
 * Thrown when API request fails
 */
export class ApiError extends Error {
  constructor(
    public code: number,
    message: string,
    public data?: unknown,
    /** HTTP status code (e.g., 401, 500) */
    public httpStatus?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Request interceptor
 * Called before sending request
 */
export type RequestInterceptor = (
  config: RequestConfig,
) => RequestConfig | Promise<RequestConfig>;

/**
 * Response interceptor
 * Called after receiving response
 */
export type ResponseInterceptor = <T>(
  response: ApiResponse<T>,
) => ApiResponse<T> | Promise<ApiResponse<T>>;

/**
 * Error interceptor
 * Called when request fails
 */
export type ErrorInterceptor = (error: ApiError) => void | Promise<void>;

/**
 * Request configuration
 */
export interface RequestConfig extends Omit<RequestInit, 'body'> {
  /** Query parameters */
  params?: Record<string, any>;
  /** Request body (will be JSON stringified) */
  body?: any;
  /** Whether to include authentication token */
  withToken?: boolean;
  /** Custom headers */
  headers?: Record<string, string>;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Zod schema for response validation */
  schema?: z.ZodTypeAny;
  /** Silent mode - skip default error handler and don't throw errors */
  silent?: boolean;
  /** Custom error handler */
  onError?: (error: ApiError) => void;
  /** Custom success handler */
  onSuccess?: (data: any) => void;
  /**
   * Raw response mode - skip extracting data from 'result' field.
   * Use this for proxy endpoints that return raw upstream responses
   * without the standard BFF code/result wrapper.
   *
   * @example
   * // For /bff/proxy/rails/api/v1/payment_methods that returns:
   * // { payment_methods: [...] }
   * const result = await bff.get('/proxy/rails/api/v1/payment_methods', {
   *   rawResponse: true
   * });
   * // result.data = { payment_methods: [...] }
   */
  rawResponse?: boolean;
}

/**
 * API Client configuration
 */
export interface ApiClientConfig {
  /** Base URL for API requests */
  baseUrl: string;
  /** Default headers */
  headers?: Record<string, string>;
  /** Default timeout in milliseconds */
  timeout?: number;
  /** Request interceptors */
  requestInterceptors?: RequestInterceptor[];
  /** Response interceptors */
  responseInterceptors?: ResponseInterceptor[];
  /** Error interceptors */
  errorInterceptors?: ErrorInterceptor[];
}

/**
 * Platform-agnostic API Client interface
 * Implementations must be provided by platform-specific packages
 */
export interface ApiClient {
  /**
   * GET request
   * @param url - Request URL (relative to baseUrl)
   * @param config - Request configuration
   */
  get<T>(url: string, config?: RequestConfig): Promise<ApiResponse<T>>;

  /**
   * POST request
   * @param url - Request URL (relative to baseUrl)
   * @param body - Request body
   * @param config - Request configuration
   */
  post<T>(
    url: string,
    body?: any,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>>;
}

/**
 * Backend Service Names
 *
 * Different backend domains/services:
 * - bff: Backend-for-Frontend service
 * - nts: NTS service
 * - oex: OEX service
 */
export type BackendService = 'bff' | 'oex' | 'nts' | 'be' | 'worker';

/**
 * Storage for multiple API clients
 * Supports different baseURLs for different services
 */
const apiClients = new Map<BackendService, ApiClient>();

/**
 * Set API client for a specific type
 *
 * @param client - The API client instance
 * @param type - The client type
 *
 * @example
 * // Set OEX client
 * setApiClient(oexClient, 'oex');
 *
 * @example
 * // Set BFF client
 * setApiClient(bffClient, 'bff');
 *
 * @example
 * // Set multiple clients
 * setApiClient(createWebApiClient({ baseUrl: '/api' }), 'oex');
 * setApiClient(createWebApiClient({ baseUrl: '/bff' }), 'bff');
 * setApiClient(createWebApiClient({ baseUrl: '/nts' }), 'nts');
 * setApiClient(createWebApiClient({ baseUrl: '/be' }), 'be');
 */
export function setApiClient(client: ApiClient, type: BackendService): void {
  apiClients.set(type, client);
}

/**
 * Get API client for a specific type
 *
 * @param type - The client type
 * @throws Error if client not initialized
 *
 * @example
 * // Get OEX client
 * const client = getApiClient('oex');
 *
 * @example
 * // Get BFF client
 * const bffClient = getApiClient('bff');
 */
export function getApiClient(type: BackendService): ApiClient {
  const client = apiClients.get(type);

  if (!client) {
    throw new Error(
      `API Client '${type}' not initialized. Call setApiClient(client, '${type}') in your entry point.`,
    );
  }

  return client;
}

/**
 * Clear a specific API client
 * Useful for testing or dynamic client management
 *
 * @param type - The backend service type
 *
 * @example
 * clearApiClient('bff');
 */
export function clearApiClient(type: BackendService): void {
  apiClients.delete(type);
}

/**
 * Clear all API clients
 * Useful for testing
 *
 * @example
 * clearAllApiClients();
 */
export function clearAllApiClients(): void {
  apiClients.clear();
}
